/**
 * Accept an invitation and land signed in — a link that ends at a login form the user has
 * no password for is a dead end.
 *
 * The invited address is not shown: the token is opaque to the client, and an endpoint
 * that turned a token into an email would be a new oracle for no benefit.
 */
import { useState, type ReactElement } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, post } from "../lib/api";
import type { SessionResponse } from "../lib/types";
import { AuthFrame } from "../components/AuthFrame";

/** Matches `user::MIN_PASSWORD_LEN` on the server. */
const MIN_PASSWORD_LENGTH = 12;
const INVALID = "This invitation is not valid. Ask whoever invited you for a new link.";

export function AcceptInvite(): ReactElement {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = params.get("token");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(token ? null : INVALID);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!token) {
      setError(INVALID);
      return;
    }
    // The common mistake costs no round trip.
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`A password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (!displayName.trim()) {
      setError("A name is required — it is what the audit trail shows.");
      return;
    }

    setError(null);
    setBusy(true);
    try {
      await post<SessionResponse>("/v1/invites/accept", {
        token,
        password,
        display_name: displayName.trim(),
      });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate("/documents", { replace: true });
    } catch (cause) {
      // Never distinguishes expired from already-used from never-existed, matching the
      // server.
      setError(
        cause instanceof ApiError && cause.status === 404
          ? INVALID
          : cause instanceof Error
            ? cause.message
            : "Could not reach the server.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame>
      <form onSubmit={submit} className="border border-hair bg-surface p-6">
        <label htmlFor="display-name" className="eyebrow">
          // your name
        </label>
        <input
          id="display-name"
          autoComplete="name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          className="mt-3 w-full border border-hair bg-ground px-3 py-2.5 font-mono text-[13px] text-text outline-none transition-colors duration-200 ease-alt placeholder:text-faint focus:border-gold"
        />

        <label htmlFor="password" className="eyebrow mt-5 block">
          // choose a password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="mt-3 w-full border border-hair bg-ground px-3 py-2.5 font-mono text-[13px] text-text outline-none transition-colors duration-200 ease-alt placeholder:text-faint focus:border-gold"
        />
        <p className="mt-2.5 text-[12px] leading-relaxed text-dim">
          At least {MIN_PASSWORD_LENGTH} characters. There is no password reset by email —
          an owner revokes and re-invites.
        </p>

        {error && (
          <p className="mt-4 border border-crit/40 bg-crit-wash px-3 py-2 text-[12.5px] leading-relaxed text-crit">
            {error}{" "}
            <Link to="/login" className="underline">
              Sign in
            </Link>
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-5 flex w-full items-center justify-center gap-2 border border-gold-surface bg-gold-surface px-3 py-2.5 font-mono text-[11px] font-bold tracking-[0.2em] text-on-gold uppercase transition-colors duration-200 ease-alt hover:bg-gold-soft disabled:opacity-50"
        >
          {busy ? "accepting…" : "accept invitation"}
        </button>
      </form>
    </AuthFrame>
  );
}
